import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { useAppData } from "../../../context/AppDataContext";
import { useAuthInfo } from "../../../context/AuthContext";
import { successfulToast, errorToast } from "../../../util/toastNotifications";
import Loading from "../../layout/Loading";
import Form from "../../layout/Form";
import FormFieldText from "../../input/FormFieldText";
import FormFieldTags from "../../input/FormFieldTags";
import Button from "../../core/Button";

import NovelEditor from "../../core/NovelEditor";
import ComboBox from "../../core/ComboBox";
import Avatar from "../../core/Avatar";
import ProjectComboBoxSelect from "../../input/ProjectComboBoxSelect";
import { resolveTaskAssigneeOrgId } from "../../../util/resolveTaskAssigneeOrgId";
import { fetchProjectsForSelect } from "../../../util/fetchProjectsForSelect";
import { parseDescriptionForEditor } from "../../../utils/parseDescriptionForEditor";
import { appendBreadcrumbSearch } from "../../../helpers/breadcrumbNavigation";

const INITIAL_FORM = {
  name: "",
  description: "",
  project_id: "",
  status_id: "",
  points_estimate: "",
  parent_task_id: "",
  priority: "low",
  started_at: "",
  due_date: "",
  tags: [],
  assigned_user_ids: [],
};

const resolveTagId = (tag) =>
  tag?.tag_id ?? tag?.id ?? tag?.tag?.tag_id ?? null;

const TaskForm = () => {
  const { taskId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const appData = useAppData();
  const api = appData?.api;
  const { userInfo } = useAuthInfo();
  const [formFields, setFormFields] = useState(INITIAL_FORM);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [task, setTask] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [parentTasks, setParentTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showAssigneeComboBox, setShowAssigneeComboBox] = useState(false);
  const [showProjectComboBox, setShowProjectComboBox] = useState(false);
  const assigneeContainerRef = useRef(null);

  const assigneeOrgId = useMemo(() => {
    const syntheticTask =
      task ||
      (formFields.project_id
        ? { project_id: formFields.project_id, org_id: null }
        : null);
    if (!syntheticTask) return null;
    return resolveTaskAssigneeOrgId(syntheticTask, projects);
  }, [task, formFields.project_id, projects]);

  const descendantTaskIds = useMemo(() => {
    if (!taskId || parentTasks.length === 0) return new Set();
    const descendants = new Set();
    const queue = [taskId];
    while (queue.length > 0) {
      const current = queue.shift();
      for (const t of parentTasks) {
        if (String(t.parent_task_id) === String(current)) {
          descendants.add(t.task_id);
          queue.push(t.task_id);
        }
      }
    }
    return descendants;
  }, [taskId, parentTasks]);

  useEffect(() => {
    if (!api || !assigneeOrgId) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    api.user
      .getUsersByOrganization(assigneeOrgId)
      .then((usersData) => {
        if (!cancelled) {
          setUsers(usersData || []);
        }
      })
      .catch((err) => {
        console.error("Error loading assignable users:", err);
        if (!cancelled) {
          setUsers([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, assigneeOrgId]);

  useEffect(() => {
    if (!api) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statusesData, taskData] = await Promise.all([
          api.taskStatus.getTaskStatuses(),
          taskId ? api.task.getTaskById(taskId) : Promise.resolve(null),
        ]);

        setTaskStatuses(statusesData);

        const params = new URLSearchParams(location.search);
        const projectIdFromUrl = params.get("project_id");
        const parentTaskIdFromUrl = params.get("parent_task_id");
        let initialProjectId = taskData?.project_id || projectIdFromUrl || null;

        if (!initialProjectId && !taskId && parentTaskIdFromUrl) {
          const parentTaskData = await api.task.getTaskById(
            parentTaskIdFromUrl
          );
          initialProjectId = parentTaskData?.project_id || null;
        }

        const projectsData = await fetchProjectsForSelect(api, {
          currentProjectId: initialProjectId,
        });
        setProjects(projectsData);

        if (taskId && taskData) {
          const uniqueTags = (taskData.tags || []).filter(
            (tag, index, self) => {
              const currentId = resolveTagId(tag);
              const currentName = (tag?.name || "").toLowerCase();
              return (
                index ===
                self.findIndex((t) => {
                  const candidateId = resolveTagId(t);
                  if (currentId != null || candidateId != null) {
                    return String(candidateId) === String(currentId);
                  }
                  return (t?.name || "").toLowerCase() === currentName;
                })
              );
            }
          );

          let taskStatusId = taskData.status_id;
          if (!taskStatusId && taskData.status) {
            taskStatusId = taskData.status.task_status_id;
          }

          let startedAtDate = "";
          if (taskData.started_at) {
            try {
              const startedDate = new Date(taskData.started_at);
              if (!isNaN(startedDate.getTime())) {
                const year = startedDate.getFullYear();
                const month = String(startedDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(startedDate.getDate()).padStart(2, "0");
                startedAtDate = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.error("Error parsing started_at:", e);
            }
          }

          let dueDateValue = "";
          if (taskData.due_date) {
            try {
              const dueDate = new Date(taskData.due_date);
              if (!isNaN(dueDate.getTime())) {
                const year = dueDate.getFullYear();
                const month = String(dueDate.getMonth() + 1).padStart(2, "0");
                const day = String(dueDate.getDate()).padStart(2, "0");
                dueDateValue = `${year}-${month}-${day}`;
              }
            } catch (e) {
              console.error("Error parsing due_date:", e);
            }
          }

          let assignedUserIds = [];
          let assignmentData = [];
          if (taskData.assignments && taskData.assignments.length > 0) {
            assignedUserIds = taskData.assignments.map((a) => a.user_id);
            assignmentData = taskData.assignments;
          } else if (
            taskData.assigned_users &&
            taskData.assigned_users.length > 0
          ) {
            assignedUserIds = taskData.assigned_users.map((u) => u.user_id);
            assignmentData = taskData.assigned_users.map((u) => ({
              user_id: u.user_id,
              user: u,
            }));
          }

          const parsedDescription = parseDescriptionForEditor(
            taskData.description
          );
          const formData = {
            name: taskData.name || "",
            description:
              parsedDescription.storedString ??
              (parsedDescription.doc
                ? JSON.stringify(parsedDescription.doc)
                : ""),
            project_id: taskData.project_id || "",
            status_id: taskStatusId || "",
            points_estimate:
              taskData.points_estimate !== null &&
              taskData.points_estimate !== undefined
                ? taskData.points_estimate.toString()
                : "",
            parent_task_id: taskData.parent_task_id || "",
            priority: taskData.priority || "low",
            started_at: startedAtDate,
            due_date: dueDateValue,
            tags: uniqueTags,
            assigned_user_ids: assignedUserIds,
          };

          setAssignments(assignmentData);

          setFormFields(formData);
          setHasLoadedData(true);

          if (taskData.project_id) {
            const tasksData = await api.task.getTasks({
              include: ["status"],
              project_id: taskData.project_id,
            });
            setParentTasks(tasksData.filter((t) => t.task_id !== taskId));
          } else if (taskData.parent_task_id) {
            const parentTaskData = await api.task.getTaskById(
              taskData.parent_task_id
            );
            if (parentTaskData.project_id) {
              const tasksData = await api.task.getTasks({
                include: ["status"],
                project_id: parentTaskData.project_id,
              });
              setParentTasks(tasksData.filter((t) => t.task_id !== taskId));
            }
          }
          setTask(taskData);
        } else {
          const formData = { ...INITIAL_FORM };

          if (projectIdFromUrl) {
            formData.project_id = projectIdFromUrl;
            const tasksData = await api.task.getTasks({
              include: ["status"],
              project_id: projectIdFromUrl,
            });
            setParentTasks(tasksData);
          }

          if (parentTaskIdFromUrl) {
            formData.parent_task_id = parentTaskIdFromUrl;
            const parentTaskData = await api.task.getTaskById(
              parentTaskIdFromUrl
            );
            if (parentTaskData.project_id && !formData.project_id) {
              formData.project_id = parentTaskData.project_id;
              const tasksData = await api.task.getTasks({
                include: ["status"],
                project_id: parentTaskData.project_id,
              });
              setParentTasks(tasksData);
            }
          }

          if (statusesData.length > 0) {
            const defaultStatus =
              statusesData.find((s) => s.name === "New") ||
              statusesData.find((s) => s.is_open) ||
              statusesData[0];
            if (defaultStatus) {
              formData.status_id = defaultStatus.task_status_id;
            }
          }

          setFormFields(formData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, taskId, location.search]);

  const validateForm = () => {
    const errors = {};

    if (!formFields.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formFields.status_id) {
      errors.status_id = "Status is required";
    }
    if (!formFields.project_id) {
      errors.project_id = "Project is required";
    }

    if (formFields.started_at && formFields.due_date) {
      const startDate = new Date(formFields.started_at);
      const dueDate = new Date(formFields.due_date);
      if (dueDate < startDate) {
        errors.due_date = "Due date must be after start date";
      }
    }

    if (formFields.points_estimate) {
      const points = parseFloat(formFields.points_estimate);
      if (isNaN(points) || points < 0) {
        errors.points_estimate = "Points estimate must be a positive number";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === "project_id") {
      setShowProjectComboBox(false);
      if (value) {
        api.task
          .getTasks({
            include: ["status"],
            project_id: value,
          })
          .then((tasksData) => {
            setParentTasks(tasksData.filter((t) => t.task_id !== taskId));
          })
          .catch((error) => {
            console.error("Error fetching tasks:", error);
          });
      } else {
        setParentTasks([]);
      }
    }
  };

  const handleProjectSelect = (projectId) => {
    handleFieldUpdate({
      target: {
        name: "project_id",
        value: projectId,
      },
    });
  };

  const handleAssigneeToggle = (userId) => {
    try {
      const currentUserIds = formFields.assigned_user_ids || [];
      let newUserIds;
      let newAssignments;
      const user = users.find((u) => u.user_id === userId);
      const isRemoving = currentUserIds.includes(userId);

      if (isRemoving) {
        newUserIds = currentUserIds.filter((id) => id !== userId);
        newAssignments = assignments.filter((a) => a.user_id !== userId);
        successfulToast("User removed from task");
      } else {
        newUserIds = [...currentUserIds, userId];
        if (user) {
          newAssignments = [
            ...assignments,
            {
              user_id: user.user_id,
              user: user,
            },
          ];
          successfulToast("User added to task");
        } else {
          newAssignments = assignments;
          errorToast("Failed to add user to task");
          return;
        }
      }

      setFormFields((prev) => ({
        ...prev,
        assigned_user_ids: newUserIds,
      }));
      setAssignments(newAssignments);
    } catch (error) {
      console.error("Error updating task assignments:", error);
      errorToast("Failed to update task assignments");
    }
  };

  const formatUserData = (user) => {
    const email = user.email || "No email";
    return `${user.first_name} ${user.last_name} (${email})`;
  };

  const handleTagRemove = async (tagId, tag) => {
    try {
      const normalizedTagId = String(tagId);
      const tagName = (tag?.name || "").toLowerCase();
      if (taskId) {
        if (tagName === "blocked" || tagName === "blocking") {
          const currentTask = task || (await api.task.getTaskById(taskId));
          const dependencyTaskIds = (currentTask?.dependencies || [])
            .map((dep) => dep?.task_id)
            .filter(Boolean);
          const dependantTaskIds = (currentTask?.dependants || [])
            .map((dep) => dep?.task_id)
            .filter(Boolean);

          await api.task.updateTaskDependencies(taskId, {
            dependency_task_ids: tagName === "blocked" ? [] : dependencyTaskIds,
            dependant_task_ids: tagName === "blocking" ? [] : dependantTaskIds,
          });

          const refreshedTask = await api.task.getTaskById(taskId);
          setTask(refreshedTask);
          setFormFields((prev) => ({
            ...prev,
            tags: refreshedTask?.tags ?? prev.tags ?? [],
            ...(refreshedTask?.version != null
              ? { version: refreshedTask.version }
              : {}),
          }));
          return;
        }

        const updated = await api.task.toggleTag(taskId, tagId);
        setFormFields((prev) => ({
          ...prev,
          tags: updated?.tags ?? prev.tags ?? [],
          ...(updated?.version != null ? { version: updated.version } : {}),
        }));
      } else {
        setFormFields((prev) => ({
          ...prev,
          tags: prev.tags.filter(
            (tag) => String(resolveTagId(tag)) !== normalizedTagId
          ),
        }));
      }
    } catch (error) {
      errorToast(error?.message || "Failed to remove tag from task");
    }
  };

  const handleAddTagToObject = async (tagId) => {
    try {
      if (taskId) {
        const updated = await api.task.toggleTag(taskId, tagId);
        setFormFields((prev) => ({
          ...prev,
          tags: updated?.tags ?? prev.tags ?? [],
          ...(updated?.version != null ? { version: updated.version } : {}),
        }));
        return;
      }

      const tagExists = formFields.tags.some(
        (tag) => String(resolveTagId(tag)) === String(tagId)
      );
      if (tagExists) {
        return;
      }

      const tagResponse = await api.tag.getTagById(tagId);
      const newTag = {
        tag_id: tagResponse.tag_id,
        name: tagResponse.name,
        color: tagResponse.color,
      };

      setFormFields((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag],
      }));
    } catch (error) {
      errorToast(error?.message || "Failed to add tag to task");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const taskData = {
        name: formFields.name,
        description: formFields.description || null,
        org_id: userInfo.org_id,
        project_id: formFields.project_id || null,
        status_id: formFields.status_id,
        points_estimate: formFields.points_estimate
          ? parseFloat(formFields.points_estimate)
          : null,
        parent_task_id: formFields.parent_task_id || null,
        priority: formFields.priority,
        started_at: formFields.started_at
          ? new Date(formFields.started_at + "T00:00:00").toISOString()
          : null,
        due_date: formFields.due_date
          ? new Date(formFields.due_date + "T00:00:00").toISOString()
          : null,
        created_by_id: userInfo.user_id,
      };

      let finalTaskId = taskId;

      if (taskId) {
        const updatedTask = await api.task.updateTask(taskId, {
          ...taskData,
          version: task.version,
        });
        finalTaskId = updatedTask.task_id;
      } else {
        const newTask = await api.task.createTask(taskData);
        finalTaskId = newTask.task_id;

        if (formFields.tags && formFields.tags.length > 0) {
          await Promise.all(
            formFields.tags.map((tag) =>
              api.task.toggleTag(finalTaskId, tag.tag_id)
            )
          );
        }
      }

      if (finalTaskId) {
        if (
          formFields.assigned_user_ids &&
          formFields.assigned_user_ids.length > 0
        ) {
          await api.task.assignUsers(finalTaskId, {
            user_ids: formFields.assigned_user_ids,
          });
        } else {
          await api.task.assignUsers(finalTaskId, []);
        }
      }

      successfulToast(
        taskId ? "Task updated successfully" : "Task created successfully"
      );
      history.push(
        appendBreadcrumbSearch(`/task/${finalTaskId}`, {
          parentType: "project",
          parentId: formFields.project_id,
          parentLabel: projects.find(
            (project) => project.project_id === formFields.project_id
          )?.name,
        })
      );
    } catch (error) {
      const message =
        error?.message ||
        (taskId ? "Failed to update task" : "Failed to create task");
      errorToast(message);
    } finally {
      setLoading(false);
    }
  };

  if (!api) {
    return <Loading />;
  }

  if (taskId && (loading || (!hasLoadedData && !formFields.name))) {
    return <Loading />;
  }

  return (
    <div className="form-styling px-16 py-8 card">
      <div className="task-form-container" key={taskId || "new"}>
        <h2>{taskId ? "Edit Task" : "New Task"}</h2>

        <Form onSubmit={handleSubmit}>
          <FormFieldText
            name="name"
            label="Task Name *"
            placeholder="Enter task name..."
            value={formFields.name}
            onChange={handleFieldUpdate}
            errorMessage={formErrors.name}
          />

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="task-form-project-trigger">Project *</label>
              <ProjectComboBoxSelect
                projects={projects}
                selectedProjectId={formFields.project_id || null}
                showComboBox={showProjectComboBox}
                onToggle={() => setShowProjectComboBox((prev) => !prev)}
                onClose={() => setShowProjectComboBox(false)}
                onSelect={handleProjectSelect}
                emptyLabel="Select a project"
                triggerId="task-form-project-trigger"
                error={Boolean(formErrors.project_id)}
              />
              {formErrors.project_id && (
                <div className="invalid-feedback">{formErrors.project_id}</div>
              )}
            </div>

            <div className="form-group col-md-6">
              <label htmlFor="status_id">Status *</label>
              <select
                id="status_id"
                name="status_id"
                value={formFields.status_id}
                onChange={handleFieldUpdate}
                className={formErrors.status_id ? "is-invalid" : ""}
              >
                <option key="status-default" value="">
                  Select a status
                </option>
                {taskStatuses.map((status, index) => (
                  <option
                    key={status.task_status_id || `status-${index}`}
                    value={status.task_status_id}
                  >
                    {status.name}
                  </option>
                ))}
              </select>
              {formErrors.status_id && (
                <div className="invalid-feedback">{formErrors.status_id}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formFields.priority}
                onChange={handleFieldUpdate}
              >
                <option value="none">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group col-md-4">
              <label htmlFor="points_estimate">Points Estimate</label>
              <select
                id="points_estimate"
                name="points_estimate"
                value={formFields.points_estimate}
                onChange={handleFieldUpdate}
                className={formErrors.points_estimate ? "is-invalid" : ""}
              >
                <option value="">Select points</option>
                <option value="0.5">0.5</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="5">5</option>
                <option value="13">13</option>
              </select>
              {formErrors.points_estimate && (
                <div className="invalid-feedback">
                  {formErrors.points_estimate}
                </div>
              )}
            </div>

            <div className="form-group col-md-4">
              <label htmlFor="parent_task_id">Parent Task</label>
              <select
                id="parent_task_id"
                name="parent_task_id"
                value={formFields.parent_task_id}
                onChange={handleFieldUpdate}
                disabled={!formFields.project_id}
                className={
                  !formFields.parent_task_id ? "parent-task-placeholder" : ""
                }
              >
                <option key="parent-default" value="">
                  {formFields.project_id
                    ? "No Parent task"
                    : "Select a project first"}
                </option>
                {parentTasks
                  .filter((t) => !descendantTaskIds.has(t.task_id))
                  .map((t, index) => (
                    <option
                      key={t.task_id || `task-${index}`}
                      value={t.task_id}
                    >
                      {t.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Assignees</label>
              <div className="task-form-assignees" ref={assigneeContainerRef}>
                <div className="task-assignments-list">
                  {assignments.map((assignment, index) => (
                    <div
                      key={assignment.user_id}
                      className="assignment-avatar"
                      style={{
                        zIndex: assignments.length - index,
                        marginLeft: index > 0 ? "-6px" : "0",
                      }}
                    >
                      <Avatar userData={assignment.user || {}} />
                    </div>
                  ))}
                  <button
                    className="assignment-add-button"
                    onClick={() => setShowAssigneeComboBox(true)}
                    aria-label="Add assignee"
                    type="button"
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                  </button>
                </div>

                {showAssigneeComboBox && (
                  <div className="assignments-combobox-wrapper">
                    <ComboBox
                      data={users}
                      selected={assignments.map((a) => a.user)}
                      valueKey="user_id"
                      placeholder="Search users..."
                      searchTopic="user"
                      formatData={formatUserData}
                      handleSelect={handleAssigneeToggle}
                      onRequestClose={() => setShowAssigneeComboBox(false)}
                      inline={true}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="form-group col-md-6">
              <label>Tags</label>
              <FormFieldTags
                selectedTags={formFields.tags}
                handleTagRemove={handleTagRemove}
                objectType="tasks"
                objectId={taskId || null}
                objectPk="task_id"
                hideLabel={true}
                handleAddTagToObject={handleAddTagToObject}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="started_at">Start Date</label>
              <input
                type="date"
                id="started_at"
                name="started_at"
                value={formFields.started_at}
                onChange={handleFieldUpdate}
                className={formErrors.started_at ? "is-invalid" : ""}
              />
              {formErrors.started_at && (
                <div className="invalid-feedback">{formErrors.started_at}</div>
              )}
            </div>

            <div className="form-group col-md-6">
              <label htmlFor="due_date">Due Date</label>
              <input
                type="date"
                id="due_date"
                name="due_date"
                value={formFields.due_date}
                onChange={handleFieldUpdate}
                className={formErrors.due_date ? "is-invalid" : ""}
              />
              {formErrors.due_date && (
                <div className="invalid-feedback">{formErrors.due_date}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <NovelEditor
              initialValue={
                parseDescriptionForEditor(formFields.description).doc
              }
              onChange={(jsonContent) => {
                setFormFields((prev) => ({
                  ...prev,
                  description: JSON.stringify(jsonContent),
                }));
              }}
            />
          </div>

          <div className="button-row">
            <div className="button-row-left">
              <Button cancel onClick={() => history.goBack()}>
                Cancel
              </Button>
            </div>
            <div className="button-row-right">
              <button type="submit" disabled={loading}>
                {loading ? "Saving..." : taskId ? "Update Task" : "Create Task"}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default TaskForm;
